import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Reveal from "@/components/Reveal";

export default function Account() {
  const { user, login, register, logout } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="font-mono text-xs tracking-[0.35em] text-fade">ALIGNING…</p>
      </div>
    );

  if (!user)
    return (
      <div data-testid="account-auth-page" className="mx-auto max-w-md px-5 pb-24 pt-40 sm:px-8">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.35em] text-fade">ACCOUNT</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-bone">{mode === "login" ? "Welcome back" : "Join the alignment"}</h1>
          <button
            data-testid="google-login-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/account";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-8 flex w-full items-center justify-center gap-3 border border-bone/40 py-4 font-display text-xs font-extrabold uppercase tracking-[0.25em] text-bone transition-all hover:bg-bone hover:text-ink"
          >
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[9px] tracking-[0.3em] text-fade">OR WITH EMAIL</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid grid-cols-2 border border-white/15">
            <button
              data-testid="auth-mode-login"
              onClick={() => setMode("login")}
              className={`py-3 font-mono text-[10px] tracking-[0.3em] transition-colors ${mode === "login" ? "bg-bone text-ink" : "text-bone/60 hover:text-bone"}`}
            >
              LOGIN
            </button>
            <button
              data-testid="auth-mode-register"
              onClick={() => setMode("register")}
              className={`py-3 font-mono text-[10px] tracking-[0.3em] transition-colors ${mode === "register" ? "bg-bone text-ink" : "text-bone/60 hover:text-bone"}`}
            >
              REGISTER
            </button>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <input
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="NAME"
                className="w-full border border-white/20 bg-transparent px-4 py-3.5 font-mono text-sm text-bone placeholder:text-bone/30 focus:border-bone focus:outline-none"
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="EMAIL"
              className="w-full border border-white/20 bg-transparent px-4 py-3.5 font-mono text-sm text-bone placeholder:text-bone/30 focus:border-bone focus:outline-none"
            />
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="PASSWORD"
              className="w-full border border-white/20 bg-transparent px-4 py-3.5 font-mono text-sm text-bone placeholder:text-bone/30 focus:border-bone focus:outline-none"
            />
            {error && (
              <p data-testid="auth-error" className="font-mono text-xs tracking-widest text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-bone py-4 font-display text-sm font-extrabold uppercase tracking-[0.25em] text-ink transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "…" : mode === "login" ? "LOGIN →" : "CREATE ACCOUNT →"}
            </button>
          </form>
        </Reveal>
      </div>
    );

  return (
    <div data-testid="account-dashboard" className="mx-auto max-w-4xl px-5 pb-24 pt-40 sm:px-8">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-fade">ACCOUNT — {user.role === "admin" ? "ADMIN" : "MEMBER"}</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-bone">{user.name || "Aligned one"}</h1>
            <p className="mt-2 font-mono text-xs tracking-widest text-fade">{user.email}</p>
          </div>
          <div className="flex gap-3">
            {user.role === "admin" && (
              <Link to="/admin" data-testid="account-admin-link" className="border border-bone/40 px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-bone transition-colors hover:bg-bone hover:text-ink">
                ADMIN PANEL
              </Link>
            )}
            <button data-testid="logout-button" onClick={logout} className="border border-white/20 px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-bone/70 transition-colors hover:border-bone hover:text-bone">
              LOGOUT
            </button>
          </div>
        </div>

        <h2 className="mt-16 font-mono text-[10px] tracking-[0.35em] text-fade">ORDER HISTORY</h2>
        <div className="mt-6">
          {orders.length === 0 ? (
            <p data-testid="orders-empty" className="border border-white/10 p-8 font-mono text-xs tracking-[0.25em] text-fade">
              NO ORDERS YET. THE NEXT ALIGNMENT IS YOURS.
            </p>
          ) : (
            orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="mb-4 border border-white/10 p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-lg font-bold text-bone">{o.id}</p>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-fade">
                    {new Date(o.created_at).toLocaleDateString()} — {o.status.toUpperCase()}
                  </p>
                </div>
                <div className="mt-4 space-y-1">
                  {o.items.map((i, idx) => (
                    <p key={idx} className="font-mono text-xs tracking-widest text-bone/70">
                      {i.qty}× {i.name} — {i.color} / {i.size}
                    </p>
                  ))}
                </div>
                <p className="mt-4 font-mono text-sm text-bone">TOTAL {fmt(o.total)}</p>
              </div>
            ))
          )}
        </div>
      </Reveal>
    </div>
  );
}
